import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { 
  Loader2, 
  Settings, 
  Users,
  CreditCard,
  Trash2,
  AlertCircle,
  UserPlus,
  ExternalLink,
  Github,
  ChevronRight,
  Shield,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { formatPlanLabel } from '@/lib/billing';
import { useAuth } from '@/contexts/AuthContext';

// Use same-origin calls in production to avoid CORS issues with cloud.doploy.io.
// In dev we allow overriding the backend URL for local testing.
const apiBase = import.meta.env.DEV ? (import.meta.env.VITE_BACKEND_URL || '') : '';
const apiPath = (path: string) => (apiBase ? `${apiBase}${path}` : path);

interface ProjectMember {
  id: string;
  role: 'administrator' | 'developer' | 'readonly';
  activated: boolean;
  created_at: string | null;
  user?: {
    id: string;
    email: string;
    full_name: string | null;
  };
  invite_email?: string;
}

interface ProjectSettings {
  id: string;
  name: string;
  odoo_version: string | null;
  github_repo: string | null;
  billing_plan: string;
  has_active_subscription: boolean;
  is_subscription_ending: boolean;
  is_subscription_cancelled: boolean;
  subscription_end_date: string | null;
  developer_branches_enabled: boolean;
  backup_storage_enabled: boolean;
  odoo_enterprise_enabled: boolean;
  odoo_enterprise_code_set: boolean;
  use_chrome_in_tests: boolean;
  subscription_id?: string | null;
  odoo_enterprise_code?: string | null;
}

interface ProjectSettingsResponse {
  project: ProjectSettings;
  members: ProjectMember[];
  user_role: string;
  is_admin: boolean;
  blocking_resources: BlockingResource[];
}

interface BlockingResource {
  type: string;
  count: number;
  summary?: string;
  details?: any;
}

async function getProjectSettings(projectId: string): Promise<ProjectSettingsResponse> {
  const response = await fetch(apiPath(`/api/project/${projectId}/settings`), {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to fetch project settings');
  return response.json();
}

async function inviteMember(projectId: string, email: string, role: string): Promise<{ success: boolean; error?: string }> {
  const response = await fetch(apiPath(`/api/project/${projectId}/invite`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, role }),
  });
  return response.json();
}

async function removeMember(projectId: string, memberId: string): Promise<{ success: boolean; error?: string }> {
  const response = await fetch(apiPath(`/api/project/${projectId}/members/${memberId}`), {
    method: 'DELETE',
    credentials: 'include',
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to remove member');
  }
  
  return response.json();
}

async function updateMemberRole(projectId: string, memberId: string, role: string): Promise<{ success: boolean; error?: string }> {
  const response = await fetch(apiPath(`/api/project/${projectId}/members/${memberId}/role`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ role }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update member role');
  }
  
  return response.json();
}

async function updateProjectSettingsApi(projectId: string, payload: Partial<Pick<ProjectSettings, 'odoo_enterprise_enabled' | 'use_chrome_in_tests'> & { odoo_enterprise_code?: string }>): Promise<any> {
  const response = await fetch(apiPath(`/api/project/${projectId}/settings`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  return response.json();
}

async function validateOdooCode(code: string) {
  const response = await fetch(apiPath('/api/project/validate-enterprise-code'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ code }),
  });
  return response.json();
}

async function cancelSubscription(projectId: string) {
  const response = await fetch(apiPath(`/api/project/${projectId}/cancel-subscription`), {
    method: 'POST',
    credentials: 'include',
  });
  return response.json();
}

async function deleteProjectApi(projectId: string) {
  const response = await fetch(apiPath(`/api/project/${projectId}/delete`), {
    method: 'POST',
    credentials: 'include',
  });
  return response.json();
}

async function openBillingPortal(projectId: string) {
  const response = await fetch(apiPath(`/api/project/${projectId}/billing-portal`), {
    method: 'POST',
    credentials: 'include',
  });
  const data = await response.json();
  if (!response.ok) {
    return { error: data?.error || 'Unable to open billing portal' };
  }
  return data;
}

function getRoleBadgeColor(role: string): string {
  switch (role) {
    case 'administrator':
    case 'owner':
      return 'bg-primary text-white';
    case 'developer':
      return 'bg-blue-100 text-blue-700';
    case 'readonly':
      return 'bg-gray-100 text-gray-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

function MemberRow({ 
  member, 
  isAdmin, 
  currentUserId,
  onRoleChange,
  onRemove,
  isUpdatingRole,
  isRemoving,
}: { 
  member: ProjectMember; 
  isAdmin: boolean;
  currentUserId: string;
  onRoleChange: (memberId: string, role: string) => void;
  onRemove: (memberId: string) => void;
  isUpdatingRole?: boolean;
  isRemoving?: boolean;
}) {
  const isCurrentUser = member.user?.id === currentUserId;
  const displayName = member.user?.full_name || member.user?.email || member.invite_email || 'Unknown';
  const displayEmail = member.user?.email || member.invite_email;

  return (
    <tr className="hover:bg-gray-50">
      <td className="py-3 px-4 border-b">
        <div>
          <p className="font-medium">{member.user?.full_name || (member.activated ? '' : 'Pending')}</p>
        </div>
      </td>
      <td className="py-3 px-4 border-b">
        <span className="text-sm">{displayEmail}</span>
      </td>
      <td className="py-3 px-4 border-b">
        {isAdmin && !isCurrentUser ? (
          <Select 
            value={member.role} 
            onValueChange={(value) => onRoleChange(member.id, value)}
            disabled={isUpdatingRole}
          >
            <SelectTrigger className="w-[150px]" disabled={isUpdatingRole}>
              {isUpdatingRole ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Updating...</span>
                </div>
              ) : (
                <SelectValue />
              )}
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="administrator">Administrator</SelectItem>
              <SelectItem value="developer">Developer</SelectItem>
              <SelectItem value="readonly">Readonly</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <Badge className={getRoleBadgeColor(member.role)}>
            {member.role === 'administrator' ? 'Administrator' : member.role.charAt(0).toUpperCase() + member.role.slice(1)}
          </Badge>
        )}
      </td>
      <td className="py-3 px-4 border-b">
        {member.activated ? (
          <Badge className="bg-green-100 text-green-700">Active</Badge>
        ) : (
          <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>
        )}
      </td>
      {isAdmin && (
        <td className="py-3 px-4 border-b">
          {!isCurrentUser && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-red-600 hover:text-red-700"
                  disabled={isRemoving}
                >
                  {isRemoving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove Member</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to remove {displayName} from this project? 
                    They will lose access immediately.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={() => onRemove(member.id)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Remove
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </td>
      )}
    </tr>
  );
}

function InviteForm({ 
  projectId, 
  onSuccess,
  onClose,
}: { 
  projectId: string; 
  onSuccess: () => void;
  onClose?: () => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('developer');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const result = await inviteMember(projectId, email, role);
      if (result.success) {
        setEmail('');
        setRole('developer');
        toast.success('Invitation sent successfully');
        onSuccess();
        onClose?.();
      } else {
        setError(result.error || 'Failed to send invitation');
      }
    } catch {
      setError('Failed to send invitation');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 space-y-2">
          <Label htmlFor="invite-email">Email Address</Label>
          <Input
            id="invite-email"
            type="email"
            placeholder="colleague@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="invite-role">Role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger id="invite-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="administrator">Administrator</SelectItem>
              <SelectItem value="developer">Developer</SelectItem>
              <SelectItem value="readonly">Readonly</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <UserPlus className="h-4 w-4 mr-2" />
          )}
          Send Invitation
        </Button>
      </DialogFooter>
    </form>
  );
}

export function ProjectSettingsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('collaborators');
  const queryClient = useQueryClient();
  const [enterpriseCode, setEnterpriseCode] = useState('');
  const [enterpriseEnabled, setEnterpriseEnabled] = useState(false);
  const [useChromeInTests, setUseChromeInTests] = useState(false);
  const [generalMessage, setGeneralMessage] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [initialCode, setInitialCode] = useState('');
  const [billingError, setBillingError] = useState<string | null>(null);
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['project-settings', projectId],
    queryFn: () => getProjectSettings(projectId!),
    enabled: !!projectId,
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: string }) => 
      updateMemberRole(projectId!, memberId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-settings', projectId] });
      toast.success('Member role updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update member role');
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (memberId: string) => removeMember(projectId!, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-settings', projectId] });
      toast.success('Member removed successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to remove member');
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: (payload: any) => updateProjectSettingsApi(projectId!, payload),
    onSuccess: (result) => {
      if (result.error) {
        setGeneralError(result.error);
        setGeneralMessage(null);
      } else {
        setGeneralMessage('Project settings updated successfully.');
        setGeneralError(null);
        queryClient.invalidateQueries({ queryKey: ['project-settings', projectId] });
      }
    },
    onError: () => setGeneralError('Failed to update settings'),
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: () => cancelSubscription(projectId!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project-settings', projectId] }),
  });

  const deleteProjectMutation = useMutation({
    mutationFn: () => deleteProjectApi(projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      window.location.href = '/projects';
    },
  });

  const billingPortalMutation = useMutation({
    mutationFn: () => openBillingPortal(projectId!),
    onSuccess: (result) => {
      if (result?.url) {
        window.location.href = result.url;
      } else if (result?.error) {
        setBillingError(result.error);
      } else {
        setBillingError('Unable to open billing portal.');
      }
    },
    onError: () => setBillingError('Unable to open billing portal.'),
  });

  useEffect(() => {
    if (!data) return;
    setEnterpriseEnabled(data.project.odoo_enterprise_enabled);
    setUseChromeInTests(data.project.use_chrome_in_tests);
    const currentCode = data.project.odoo_enterprise_code || '';
    setEnterpriseCode(currentCode);
    setInitialCode(currentCode);
  }, [data]);

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Failed to load project settings. You may not have permission to view this page.
          </AlertDescription>
        </Alert>
      </DashboardLayout>
    );
  }

  const { project, members, is_admin } = data;
  const blockingResources = data.blocking_resources || [];
  const planLabel = formatPlanLabel(project.billing_plan);

  const handleSaveGeneral = () => {
    if (!is_admin) return;
    setGeneralError(null);
    setGeneralMessage(null);
    setValidationMessage(null);
    setValidationError(null);
    const payload: any = {
      odoo_enterprise_enabled: enterpriseEnabled,
      use_chrome_in_tests: useChromeInTests,
    };
    const trimmedCode = enterpriseCode.trim();
    const codeChanged = trimmedCode !== initialCode;
    if (codeChanged) {
      payload.odoo_enterprise_code = trimmedCode;
    }
    updateSettingsMutation.mutate(payload);
  };

  const sidebarItems = [
    { id: 'collaborators', label: 'Collaborators', icon: Users },
    { id: 'general', label: 'General', icon: Settings },
    ...(is_admin ? [
      { id: 'billing', label: 'Billing', icon: CreditCard },
      { id: 'danger', label: 'Danger Zone', icon: Trash2 },
    ] : []),
  ];

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Project Settings
          </h1>
          <p className="text-gray-500 mt-1">
            Manage collaborators and project configuration for {project.name}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="">
              <div className="px-4 py-1 border-b">
                <CardTitle className="text-base">Settings</CardTitle>
              </div>
              <CardContent className="p-0">
                <nav className="flex flex-col gap-1 p-2">
                  {sidebarItems.map((item) => {
                    const isActive = activeSection === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => setActiveSection(item.id)}
                        className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-left transition-colors border ${
                          isActive
                            ? 'border-primary/50 bg-primary/10 text-primary'
                            : 'border-transparent text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <item.icon className="h-4 w-4" />
                        <span className="truncate">{item.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 max-w-4xl space-y-6">
            {/* Collaborators Section */}
            {activeSection === 'collaborators' && (
              <Card>
                <CardHeader>
                  <CardTitle>Project Collaborators</CardTitle>
                  <CardDescription>
                    Manage the team members who have access to this project.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Roles Info */}
                  <Alert className="mb-4">
                    <Shield className="h-4 w-4" />
                    <AlertTitle>Roles and Permissions</AlertTitle>
                    <AlertDescription>
                      <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                        <li><strong>Administrator</strong>: Full control. Can manage billing, servers, deployments, backups, and team.</li>
                        <li><strong>Developer</strong>: Full control over deployments, branches, servers, and backups. Cannot manage billing.</li>
                        <li><strong>Readonly</strong>: Can view dashboards and deployments. Cannot change settings or create/destroy resources.</li>
                      </ul>
                    </AlertDescription>
                  </Alert>

                  {/* Members Table */}
                  {members.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Name</th>
                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Email</th>
                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Role</th>
                            <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b">Status</th>
                            {is_admin && (
                              <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 border-b w-16">Actions</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {members.map((member) => (
                            <MemberRow
                              key={member.id}
                              member={member}
                              isAdmin={is_admin}
                              currentUserId={user?.id || ''}
                              onRoleChange={(memberId, role) => 
                                updateRoleMutation.mutate({ memberId, role })
                              }
                              onRemove={(memberId) => 
                                removeMemberMutation.mutate(memberId)
                              }
                              isUpdatingRole={updateRoleMutation.isPending && updateRoleMutation.variables?.memberId === member.id}
                              isRemoving={removeMemberMutation.isPending && removeMemberMutation.variables === member.id}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No collaborators yet</p>
                    </div>
                  )}

                  {/* Invite Form */}
                  {is_admin && (
                    <div className="mt-6 pt-6 border-t">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">Invite New Collaborator</h4>
                          <p className="text-sm text-gray-500">Send an invite to join this project.</p>
                        </div>
                        <Button onClick={() => setInviteOpen(true)} variant="default">
                          <UserPlus className="h-4 w-4 mr-2" />
                          Invite
                        </Button>
                      </div>

                      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Invite Collaborator</DialogTitle>
                            <DialogDescription>
                              Enter the collaborator&apos;s email and role. They&apos;ll receive an invitation to join.
                            </DialogDescription>
                          </DialogHeader>
                          <InviteForm 
                            projectId={projectId!} 
                            onSuccess={() => {
                              queryClient.invalidateQueries({ queryKey: ['project-settings', projectId] });
                            }}
                            onClose={() => setInviteOpen(false)}
                          />
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* General Settings Section */}
            {activeSection === 'general' && (
              <Card>
                <CardHeader>
                  <CardTitle>General Settings</CardTitle>
                  <CardDescription>
                    Basic project configuration
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {generalMessage && (
                    <Alert className="bg-green-50 border-green-200">
                      <AlertDescription className="text-green-800">{generalMessage}</AlertDescription>
                    </Alert>
                  )}
                  {generalError && (
                    <Alert variant="destructive">
                      <AlertDescription>{generalError}</AlertDescription>
                    </Alert>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label>Project Name</Label>
                      <Input value={project.name} disabled className="mt-1" />
                      <p className="text-xs text-gray-500 mt-1">Project name cannot be changed after creation.</p>
                    </div>
                    
                    <div>
                      <Label>Odoo Version</Label>
                      <Input value={project.odoo_version || 'Not set'} disabled className="mt-1" />
                    </div>
                  </div>

                  <div>
                    <Label>GitHub Repository</Label>
                    <div className="flex mt-1">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                        github.com/
                      </span>
                      <Input 
                        value={project.github_repo || ''} 
                        disabled 
                        className="rounded-l-none flex-1" 
                      />
                      {project.github_repo && (
                        <a
                          href={`https://github.com/${project.github_repo}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2"
                        >
                          <Button variant="outline" type="button">
                            <Github className="h-4 w-4 mr-1" />
                            View
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4 pt-2 border-t">
                    <div className="space-y-2">
                      <Label htmlFor="enterprise-code">Odoo Enterprise Subscription Code</Label>
                      <div className="flex gap-2">
                        <Input
                          id="enterprise-code"
                          type={showCode ? 'text' : 'password'}
                          placeholder={project.odoo_enterprise_code_set ? '•••••• (code set)' : 'Enter subscription code'}
                          value={enterpriseCode}
                          onChange={(e) => setEnterpriseCode(e.target.value)}
                          disabled={!is_admin}
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => setShowCode((prev) => !prev)}
                          disabled={!is_admin}
                        >
                          {showCode ? 'Hide' : 'Show'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={async () => {
                            setValidationMessage(null);
                            setValidationError(null);
                            setIsValidating(true);
                            try {
                              const codeToValidate = (enterpriseCode || initialCode).trim();
                              if (!codeToValidate) {
                                setValidationError('Enter a subscription code first');
                                setIsValidating(false);
                                return;
                              }
                              const result = await validateOdooCode(codeToValidate);
                              if (result.valid) {
                                setValidationMessage(result.message || 'Subscription code is valid');
                                setValidationError(null);
                              } else {
                                setValidationError(result.error || 'Validation failed');
                                setValidationMessage(null);
                              }
                            } catch {
                              setValidationError('Validation failed');
                              setValidationMessage(null);
                            } finally {
                              setIsValidating(false);
                            }
                          }}
                          disabled={!is_admin || isValidating || !(enterpriseCode || project.odoo_enterprise_code_set)}
                        >
                          {isValidating ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Validating...
                            </>
                          ) : (
                            'Validate'
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500">
                        Enter a code to validate enterprise builds. Leave blank to keep the existing code.
                      </p>
                      {validationMessage && (
                        <Alert className="bg-green-50 border-green-200">
                          <AlertDescription className="text-green-800">{validationMessage}</AlertDescription>
                        </Alert>
                      )}
                      {validationError && (
                        <Alert variant="destructive">
                          <AlertDescription>{validationError}</AlertDescription>
                        </Alert>
                      )}
                    </div>

                    <div className="flex items-center justify-between border rounded px-3 py-2">
                      <div>
                        <p className="font-medium text-sm">Enable Odoo Enterprise</p>
                        <p className="text-xs text-gray-500">
                          Requires a valid subscription code. Applies to new builds.
                        </p>
                      </div>
                      <Switch
                        checked={enterpriseEnabled}
                        onCheckedChange={setEnterpriseEnabled}
                        disabled={!is_admin}
                      />
                    </div>

                    {project.developer_branches_enabled && (
                      <div className="flex items-center justify-between border rounded px-3 py-2">
                        <div>
                          <p className="font-medium text-sm">Use Chrome in Tests</p>
                          <p className="text-xs text-gray-500">Enable browser-based tests in developer builds.</p>
                        </div>
                        <Switch
                          checked={useChromeInTests}
                          onCheckedChange={setUseChromeInTests}
                          disabled={!is_admin}
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
                <CardFooter className="border-t flex justify-end">
                  <Button 
                    onClick={handleSaveGeneral}
                    disabled={!is_admin || updateSettingsMutation.isPending}
                  >
                    {updateSettingsMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Settings'
                    )}
                  </Button>
                </CardFooter>
              </Card>
            )}

            {/* Billing Section */}
            {activeSection === 'billing' && is_admin && (
              <Card>
                <CardHeader>
                  <CardTitle>Project Billing</CardTitle>
                  <CardDescription>
                    Manage your subscription and payment details
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {billingPortalMutation.isError && (
                    <Alert variant="destructive">
                      <AlertDescription>Unable to open billing portal. Please try again.</AlertDescription>
                    </Alert>
                  )}
                  {billingError && (
                    <Alert variant="destructive">
                      <AlertDescription>{billingError}</AlertDescription>
                    </Alert>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-500">Current Plan</Label>
                      <p className="font-medium text-lg">{planLabel}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Status</Label>
                      <div className="mt-1">
                        {project.has_active_subscription ? (
                          <Badge className="bg-green-100 text-green-700">Active</Badge>
                        ) : project.is_subscription_cancelled ? (
                          <Badge className="bg-red-100 text-red-700">Cancelled</Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-700">No Subscription</Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {project.is_subscription_ending && project.subscription_end_date && (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>Subscription Ending</AlertTitle>
                      <AlertDescription>
                        Your subscription will end on {new Date(project.subscription_end_date).toLocaleDateString()}.
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
                <CardFooter className="border-t p-4 rounded-b-xl -mb-px flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setBillingError(null);
                      billingPortalMutation.mutate();
                    }}
                    disabled={billingPortalMutation.isPending}
                  >
                    {billingPortalMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Opening...
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-4 w-4 mr-2" />
                        Manage Billing
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            )}

            {/* Danger Zone */}
            {activeSection === 'danger' && is_admin && (
              <Card className="overflow-hidden rounded-xl border-red-200 p-0">
                <CardHeader className="bg-red-50 border-b border-red-200 p-4 rounded-t-xl">
                  <CardTitle className="text-red-700 flex items-center gap-2">
                    <Trash2 className="h-5 w-5" />
                    Danger Zone
                  </CardTitle>
                  <CardDescription className="text-red-600">
                    These actions are destructive and cannot be undone.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {blockingResources.length > 0 && (
                    <Alert className="mb-4">
                      <AlertDescription>
                        Remove the following resources before deleting or cancelling subscription:
                        <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                          {blockingResources.map((resource, idx) => (
                            <li key={idx}>
                              {resource.summary || `${resource.count} ${resource.type}`}
                            </li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="flex items-center justify-between py-4 border-b">
                    <div>
                      <h4 className="font-medium">Cancel subscription</h4>
                      <p className="text-sm text-gray-500">
                        Stop billing charges for this project. You must cancel before deletion.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => cancelSubscriptionMutation.mutate()}
                      disabled={cancelSubscriptionMutation.isPending || blockingResources.length > 0 || !project.subscription_id}
                    >
                      {cancelSubscriptionMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        'Cancel Subscription'
                      )}
                    </Button>
                  </div>

                  <div className="flex items-center justify-between py-4">
                    <div>
                      <h4 className="font-medium">Delete this project</h4>
                      <p className="text-sm text-gray-500">
                        Permanently delete this project and all associated data including deployments, backups, and settings.
                      </p>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="destructive"
                          disabled={deleteProjectMutation.isPending || blockingResources.length > 0 || !!project.subscription_id}
                        >
                          {deleteProjectMutation.isPending ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            'Delete Project'
                          )}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete the project 
                            <strong className="text-foreground"> {project.name}</strong> and all 
                            associated resources including deployments, backups, and settings.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            className="bg-red-600 hover:bg-red-700"
                            onClick={() => deleteProjectMutation.mutate()}
                          >
                            Yes, delete project
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
